import { Account } from '@/client/accounts';
import { Actions, Subjects } from '@/client/users';
import {
  LAST_SELECTED_ACCOUNT_SETTINGS,
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { ViewOnlyRestriction } from '@/components/custom-roles';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AccountTabs } from './AccountTabs';

const StyledToggleContainer = styled(FlexContainer)`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 500;
  flex: 0 0 25%;
  min-width: max-content;
`;

const StyledUnsavedText = styled.span`
  font-size: var(--xsmall-font-size);
  color: var(--red-main);
  font-weight: 500;
  text-align: right;
  position: absolute;
  bottom: calc(var(--default-padding) * -1);
  white-space: nowrap;
`;

const StyledDialog = styled(Dialog)`
  width: 600px;
  max-width: 90vw;
`;

const StyledModalBody = styled.div`
  margin: 0;
`;

const StyledModalButtons = styled(FlexContainer)`
  && .p-button {
    flex-shrink: 1;
    min-width: 0;
    max-width: 100%;
    height: auto;
    min-height: var(--default-input-height);
    white-space: normal;
  }
`;

const StyledModalText = styled.p`
  margin: 0;
  margin-bottom: var(--medium-big-padding);
`;

type AccountHeaderPropsType = {
  selectedAccount: Account | undefined;
  isCreate?: boolean;
  title?: string;
  setShowSystemSettings?: React.Dispatch<React.SetStateAction<boolean>>;
  headerActions?: React.ReactNode;
  isDirty?: boolean;
  onSave?: () => Promise<boolean | void>;
  isSaving?: boolean;
};

export const AccountHeader: React.FC<AccountHeaderPropsType> = ({
  selectedAccount,
  isCreate,
  title,
  setShowSystemSettings,
  headerActions,
  isDirty,
  onSave,
  isSaving,
}) => {
  const { t } = useTranslation();
  const { isRestrictedByRole } = usePermission();
  const isSaveRestricted = isRestrictedByRole(
    Actions.UPDATE,
    Subjects.OWN_ACCOUNT,
  );
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const location = useLocation();
  const navigate = useNavigate();

  const [pendingNavigate, setPendingNavigate] = useState<(() => void) | null>(
    null,
  );
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isSavingAndLeaving, setIsSavingAndLeaving] = useState(false);

  const handleNavigate = (url: string) => {
    if (isDirty) {
      setPendingNavigate(() => () => navigate(url));
      setShowUnsavedModal(true);
    } else {
      navigate(url);
    }
  };

  const handleSaveAndLeave = async () => {
    if (!pendingNavigate) return;
    setIsSavingAndLeaving(true);
    const result = await onSave?.();
    setIsSavingAndLeaving(false);
    if (result === false) {
      // Save failed (API error or validation error) — stay on page.
      setShowUnsavedModal(false);
      setPendingNavigate(null);
      return;
    }
    pendingNavigate();
    setShowUnsavedModal(false);
    setPendingNavigate(null);
  };

  const handleLeaveWithoutSaving = () => {
    if (!pendingNavigate) return;
    pendingNavigate();
    setShowUnsavedModal(false);
    setPendingNavigate(null);
  };

  // Intercept all internal navigation link clicks when dirty
  useEffect(() => {
    if (!isDirty) return;

    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest<HTMLAnchorElement>(
        'a[href]',
      );
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('javascript:') ||
        href.startsWith('mailto:')
      )
        return;

      if (!href.startsWith('/') && !href.startsWith(window.location.origin))
        return;

      if (anchor.getAttribute('target') === '_blank') return;

      e.preventDefault();
      e.stopPropagation();

      const path = href.startsWith('/')
        ? href
        : new URL(href).pathname + new URL(href).search;
      setPendingNavigate(() => () => navigate(path));
      setShowUnsavedModal(true);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isDirty, navigate]);

  // Intercept browser back/forward button when dirty
  const isHandlingPopState = useRef(false);
  useEffect(() => {
    if (!isDirty) return;

    const handlePopState = () => {
      if (isHandlingPopState.current) return;
      isHandlingPopState.current = true;
      window.history.go(1);
      setTimeout(() => {
        isHandlingPopState.current = false;
      }, 100);
      setPendingNavigate(() => () => navigate(-1));
      setShowUnsavedModal(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty, navigate]);

  // Intercept browser close/refresh when dirty
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const lastSelected =
    sessionStorage.getItem(LAST_SELECTED_ACCOUNT_SETTINGS) === 'true';
  const [systemSettingsEnabled, setSystemSettingsEnabled] =
    useState(lastSelected);

  useEffect(() => {
    if (!setShowSystemSettings) return;
    setShowSystemSettings(systemSettingsEnabled);
  }, [systemSettingsEnabled]);

  const navigateToDetailsIfTabIsNotPresent = () => {
    if (
      ![
        RedirectPaths[RedirectPathsEnum.ACCOUNT](selectedAccount?.id as string),
        RedirectPaths[RedirectPathsEnum.ACCOUNT_AZURE_AD](
          selectedAccount?.id as string,
        ),
        RedirectPaths[RedirectPathsEnum.ACCOUNT_SSO](
          selectedAccount?.id as string,
        ),
      ].includes(location.pathname)
    ) {
      navigate(
        RedirectPaths[RedirectPathsEnum.ACCOUNT](selectedAccount?.id as string),
      );
    }
  };

  return (
    <>
      <FlexContainer gap="var(--big-padding)" justify="space-between">
        <h1 className="mt-4 mb-4 flex-shrink-0">
          {title ?? selectedAccount?.name ?? currentAccount?.name}
        </h1>
        <FlexContainer
          gap="var(--medium-padding)"
          align="center"
          justify="flex-end"
          width="auto"
        >
          {!currentAccount?.isSystem && currentUser?.account.isSystem && (
            <StyledToggleContainer gap={12} align="center" justify="flex-end">
              <div>{t('account.systemSettings')}</div>
              <InputSwitch
                className="v2"
                checked={systemSettingsEnabled}
                onChange={() => {
                  setSystemSettingsEnabled(!systemSettingsEnabled);
                  sessionStorage.setItem(
                    LAST_SELECTED_ACCOUNT_SETTINGS,
                    `${!systemSettingsEnabled}`,
                  );
                  navigateToDetailsIfTabIsNotPresent();
                }}
              />
            </StyledToggleContainer>
          )}
          {headerActions}

          {onSave && (
            <FlexContainer
              direction="column"
              align="flex-end"
              gap={4}
              width="auto"
              className="relative"
            >
              {isSaveRestricted ? (
                <ViewOnlyRestriction>
                  <AppButton label={t('button.saveSettings')} isDisabled />
                </ViewOnlyRestriction>
              ) : (
                <AppButton
                  label={t('button.saveSettings')}
                  state={
                    isSaving ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                  }
                  onClick={onSave}
                />
              )}
              {isDirty && (
                <StyledUnsavedText data-id="unsavedChanges">
                  {t('account.unsavedChanges')}
                </StyledUnsavedText>
              )}
            </FlexContainer>
          )}
        </FlexContainer>
      </FlexContainer>
      {selectedAccount && (
        <AccountTabs
          account={selectedAccount}
          systemSettingsEnabled={systemSettingsEnabled}
          onNavigate={handleNavigate}
        />
      )}
      {currentAccount && !selectedAccount && !isCreate && (
        <AccountTabs
          account={currentAccount}
          systemSettingsEnabled={systemSettingsEnabled}
          onNavigate={handleNavigate}
        />
      )}
      <StyledDialog
        draggable={false}
        visible={showUnsavedModal}
        header={t('account.unsavedChanges')}
        modal
        onHide={() => {
          setShowUnsavedModal(false);
          setPendingNavigate(null);
        }}
        className="v2"
      >
        <StyledModalBody>
          <StyledModalText
            dangerouslySetInnerHTML={{
              __html: t('account.unsavedChanges.body'),
            }}
          />
          <StyledModalButtons
            gap="var(--default-padding)"
            justify="flex-start"
            wrap="wrap"
          >
            <AppButton
              label={t('account.unsavedChanges.saveAndLeave')}
              state={
                isSavingAndLeaving
                  ? LoadingStatuses.LOADING
                  : LoadingStatuses.IDLE
              }
              onClick={handleSaveAndLeave}
            />
            <AppButton
              label={t('account.unsavedChanges.leaveWithoutSaving')}
              type="outlined"
              severity="secondary"
              onClick={handleLeaveWithoutSaving}
            />
          </StyledModalButtons>
        </StyledModalBody>
      </StyledDialog>
    </>
  );
};
